import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, BriefcaseBusiness, Check, ChevronLeft, Clock3, Compass, Flame, KeyRound, RotateCcw, Share2, ShieldCheck, Sparkles, Target, Users, Volume2, VolumeX } from "lucide-react";
import { ActivationModal } from "./components/ActivationModal";
import { CardFace } from "./components/CardFace";
import { VentRoom } from "./components/VentRoom";
import { XhsShareModal } from "./components/XhsShareModal";
import { api, clearToken, readToken } from "./services/api";
import { playCardFlipSound, playCardPickSound, preloadInteractionSounds, startShuffleSound } from "./services/interactionSounds";
import type { ActionArtifact, DrawnCard, Reading, Scenario, SessionInfo } from "./types";

type Step = "welcome" | "question" | "vent" | "shuffle" | "select" | "reveal" | "reading";
const scenarios: Array<{ name: Scenario; icon: typeof Target; desc: string }> = [
  { name: "方向探索", icon: Compass, desc: "没有明确目标，先找值得验证的方向" },
  { name: "去留选择", icon: Target, desc: "该坚持、调整，还是离开？" },
  { name: "Offer比较", icon: BriefcaseBusiness, desc: "不只看薪资，比较真实代价" },
  { name: "晋升加薪", icon: Sparkles, desc: "找到阻力与可推进的抓手" },
  { name: "职场关系", icon: Users, desc: "看清边界、沟通与协作" },
];
const careerArtifactOptions = ["7天行动计划", "关键沟通话术", "去留决策清单", "风险止损方案"];
const directionArtifactOptions = ["7天方向验证计划", "三方向对比表", "简历定位与自我介绍", "从业者访谈话术"];
const directionStages = ["应届 / 在校", "工作 1–3 年", "想转行", "空窗后重启"];
const directionStrengths = ["喜欢和人打交道", "擅长分析和整理", "喜欢创造与表达", "动手解决具体问题", "追求稳定与秩序", "愿意尝试新事物"];
const directionAvoids = ["高压销售", "重复事务", "频繁加班", "复杂人际", "收入波动", "长期独处"];

export default function App() {
  const [step, setStep] = useState<Step>("welcome");
  const [scenario, setScenario] = useState<Scenario>("去留选择");
  const [question, setQuestion] = useState("");
  const [context, setContext] = useState("");
  const [careerStage, setCareerStage] = useState(directionStages[0]);
  const [strengthPreference, setStrengthPreference] = useState<string[]>([]);
  const [avoidPreference, setAvoidPreference] = useState<string[]>([]);
  const [horizon, setHorizon] = useState("未来30天");
  const [drawId, setDrawId] = useState("");
  const [slotCount, setSlotCount] = useState(18);
  const [chosen, setChosen] = useState<number[]>([]);
  const [cards, setCards] = useState<DrawnCard[]>([]);
  const [revealed, setRevealed] = useState(0);
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [activationOpen, setActivationOpen] = useState(false);
  const [report, setReport] = useState<Reading | null>(null);
  const [artifact, setArtifact] = useState<ActionArtifact | null>(null);
  const [artifactType, setArtifactType] = useState(careerArtifactOptions[0]);
  const [loading, setLoading] = useState("");
  const [error, setError] = useState("");
  const [shareMode, setShareMode] = useState<"report" | "artifact" | null>(null);
  const [soundOn, setSoundOn] = useState(true);
  const shuffleStopRef = useRef<null|(()=>void)>(null);

  useEffect(() => {
    if (!readToken()) return;
    api.session().then((value) => setSession({ ...value, token: readToken() })).catch(() => clearToken());
  }, []);

  useEffect(() => { preloadInteractionSounds(); }, []);

  useEffect(() => () => shuffleStopRef.current?.(), []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [step]);

  const canContinue = question.trim().length >= 8;
  const progress = useMemo(() => ({ welcome: 0, question: 20, vent: 20, shuffle: 40, select: 60, reveal: 80, reading: 100 }[step]), [step]);
  const activeArtifactOptions = scenario === "方向探索" ? directionArtifactOptions : careerArtifactOptions;
  const enrichedContext = scenario === "方向探索"
    ? [`当前阶段：${careerStage}`, strengthPreference.length ? `偏好与优势：${strengthPreference.join("、")}` : "", avoidPreference.length ? `希望避开：${avoidPreference.join("、")}` : "", context.trim() ? `补充经历：${context.trim()}` : ""].filter(Boolean).join("；")
    : context;

  function selectScenario(next: Scenario) {
    setScenario(next);
    if (next === "方向探索") {
      setArtifactType(directionArtifactOptions[0]);
      if (!question.trim()) setQuestion("结合我的经历和偏好，我现阶段最值得优先验证哪些职业方向？");
    } else {
      setArtifactType(careerArtifactOptions[0]);
      if (question.startsWith("结合我的经历和偏好")) setQuestion("");
    }
  }

  function toggleDirectionChoice(value: string, kind: "strength" | "avoid") {
    const setter = kind === "strength" ? setStrengthPreference : setAvoidPreference;
    setter((previous) => previous.includes(value) ? previous.filter((item) => item !== value) : previous.length < 3 ? [...previous, value] : previous);
  }

  async function beginShuffle() {
    setError("");
    if (soundOn) {
      shuffleStopRef.current?.();
      shuffleStopRef.current = startShuffleSound();
    }
    setStep("shuffle");
    try {
      const result = await api.startDraw(scenario === "方向探索" ? "direction" : "career");
      setDrawId(result.drawId);
      setSlotCount(result.slotCount);
    } catch (err) {
      shuffleStopRef.current?.();
      shuffleStopRef.current = null;
      setError(err instanceof Error ? err.message : "洗牌服务暂时不可用");
      setStep("question");
    }
  }

  async function stopShuffle() {
    if (!drawId) return;
    shuffleStopRef.current?.();
    shuffleStopRef.current = null;
    setChosen([]);
    setStep("select");
  }

  function chooseSlot(index: number) {
    if (soundOn && (chosen.includes(index) || chosen.length < 3)) playCardPickSound();
    setChosen((previous) => previous.includes(index) ? previous.filter((item) => item !== index) : previous.length < 3 ? [...previous, index] : previous);
  }

  async function revealCards() {
    setLoading("正在锁定你的选择…");
    setError("");
    try {
      const result = await api.selectCards(drawId, chosen);
      setCards(result.cards);
      setStep("reveal");
      setRevealed(0);
    } catch (err) {
      setError(err instanceof Error ? err.message : "翻牌失败");
    } finally {
      setLoading("");
    }
  }

  function flipNext() {
    const next = Math.min(3, revealed + 1);
    if (next !== revealed && soundOn) playCardFlipSound();
    setRevealed(next);
  }

  async function generateReport() {
    if (!session) return setActivationOpen(true);
    setLoading("正在把牌面翻译成你的职场方案…");
    setError("");
    try {
      const result = await api.report({ drawId, scenario, question, context: enrichedContext, horizon, cards });
      setReport(result.report);
      setSession((value) => value ? { ...value, quotaLeft: result.quotaLeft } : value);
      setStep("reading");
      saveHistory(result.report, cards, question, scenario);
    } catch (err) {
      setError(err instanceof Error ? err.message : "生成失败");
      if ((err instanceof Error ? err.message : "").includes("激活")) setActivationOpen(true);
    } finally {
      setLoading("");
    }
  }

  async function generateArtifact() {
    if (!report) return;
    if (!session) return setActivationOpen(true);
    setLoading(`正在生成${artifactType}…`);
    setError("");
    try {
      const result = await api.actionPlan({ drawId, scenario, question, context: enrichedContext, cards, report, artifactType });
      setArtifact(result.artifact);
      setSession((value) => value ? { ...value, quotaLeft: result.quotaLeft } : value);
    } catch (err) {
      setError(err instanceof Error ? err.message : "生成失败");
    } finally {
      setLoading("");
    }
  }

  function restart() {
    setStep("question"); setDrawId(""); setChosen([]); setCards([]); setRevealed(0); setReport(null); setArtifact(null); setError("");
  }

  function toggleSound() {
    const next = !soundOn;
    setSoundOn(next);
    if (!next) {
      shuffleStopRef.current?.();
      shuffleStopRef.current = null;
    } else if (step === "shuffle") {
      shuffleStopRef.current = startShuffleSound();
    }
  }

  return <div className="app-shell">
    <header className="topbar">
      <button className="brand" onClick={() => setStep("welcome")}><span>✦</span><div>职场答案之书<small>CAREER CARDS</small></div></button>
      <button className="quota-chip" onClick={() => setActivationOpen(true)}><KeyRound size={15} />{session ? `剩余 ${session.quotaLeft} 次` : "激活权益"}</button>
    </header>
    {step !== "welcome" && <div className="progress"><i style={{ width: `${progress}%` }} /></div>}

    <main>
      {step === "welcome" && <Welcome onStart={() => setStep("question")} />}
      {step === "vent" && <VentRoom session={session} onNeedActivation={() => setActivationOpen(true)} onQuotaChange={(quotaLeft) => setSession((value) => value ? { ...value, quotaLeft } : value)} onBack={() => setStep("question")} />}
      {step === "question" && <section className="stage narrow">
        <button className="back" onClick={() => setStep("welcome")}><ChevronLeft size={18} />返回</button>
        <p className="eyebrow">01 · 先聚焦问题</p>
        <h1>你现在最想看清什么？</h1>
        <p className="lead">问题越具体，行动建议越有用。我们不替你做决定，只帮你把信息与选择摊开。</p>
        <div className="scenario-grid">{scenarios.map(({ name, icon: Icon, desc }) => <button key={name} className={`scenario ${name === "方向探索" ? "direction-scenario" : ""} ${scenario === name ? "active" : ""}`} onClick={() => selectScenario(name)}><Icon /><span><b>{name}</b><small>{desc}</small></span>{scenario === name && <Check className="check" />}</button>)}</div>
        <button className="vent-entry" onClick={() => setStep("vent")}><span className="vent-entry-icon"><Flame /></span><span><b>职场泄压室 · 情绪释放练习</b><small>碰到离谱客户或同事？先处理烦心事，再拿现实解决方案</small></span><ArrowRight /></button>
        {scenario === "方向探索" && <section className="direction-guide">
          <div className="direction-guide-head"><Compass/><div><b>没方向也能开始</b><small>不要求你先说出职业名，选几个最接近你的线索即可。</small></div></div>
          <div className="choice-group"><span>你现在处于哪个阶段？</span><div>{directionStages.map((item)=><button key={item} className={careerStage===item?"active":""} onClick={()=>setCareerStage(item)}>{item}</button>)}</div></div>
          <div className="choice-group"><span>哪些描述更像你？<em>最多选 3 项</em></span><div>{directionStrengths.map((item)=><button key={item} className={strengthPreference.includes(item)?"active":""} onClick={()=>toggleDirectionChoice(item,"strength")}>{strengthPreference.includes(item)&&<Check/>}{item}</button>)}</div></div>
          <div className="choice-group"><span>你明确不想要什么？<em>最多选 3 项</em></span><div>{directionAvoids.map((item)=><button key={item} className={avoidPreference.includes(item)?"active":""} onClick={()=>toggleDirectionChoice(item,"avoid")}>{avoidPreference.includes(item)&&<Check/>}{item}</button>)}</div></div>
        </section>}
        <label className="field"><span>{scenario === "方向探索" ? "这次想先解决的问题" : "你的具体问题"} <em>{question.length}/120</em></span><textarea maxLength={120} value={question} onChange={(e) => setQuestion(e.target.value)} placeholder={scenario === "方向探索" ? "例如：我没有明确目标，想知道该从哪些方向开始低成本尝试。" : "例如：团队近期频繁调整，我该继续争取晋升，还是开始找新机会？"} /></label>
        <label className="field"><span>{scenario === "方向探索" ? "你的经历或尝试（可选）" : "补充背景（可选）"} <em>{context.length}/300</em></span><textarea className="small-area" maxLength={300} value={context} onChange={(e) => setContext(e.target.value)} placeholder={scenario === "方向探索" ? "学过什么、做过哪些项目或兼职、别人常夸你什么……没有正式工作经历也没关系。" : "你的岗位、已尝试过什么、不能接受的底线…"} /></label>
        <div className="horizons">{["未来7天", "未来30天", "未来3个月"].map((item) => <button key={item} className={horizon === item ? "active" : ""} onClick={() => setHorizon(item)}><Clock3 size={15} />{item}</button>)}</div>
        {error && <p className="error">{error}</p>}
        <button className="primary full" disabled={!canContinue} onClick={() => void beginShuffle()}>开始洗牌 <ArrowRight size={18} /></button>
      </section>}

      {step === "shuffle" && <section className="stage ritual center">
        <button className="stage-sound-toggle" onClick={toggleSound}>{soundOn ? <Volume2 size={16}/> : <VolumeX size={16}/>} {soundOn ? "声音已开" : "声音已关"}</button>
        <p className="eyebrow">02 · 把注意力放回问题</p><h1>牌正在重新排列</h1><p className="lead">想一遍你的问题，觉得“可以了”时亲手叫停。</p>
        <div className="shuffle-deck" aria-label="洗牌动画">{[0,1,2,3,4].map((i) => <div className="shuffle-card" key={i} style={{ "--i": i } as React.CSSProperties}><span>✦</span></div>)}</div>
        <button className="primary stop" disabled={!drawId} onClick={() => void stopShuffle()}>{drawId ? "停止洗牌" : "正在准备牌组…"}</button>
      </section>}

      {step === "select" && <section className="stage wide center">
        <button className="stage-sound-toggle" onClick={toggleSound}>{soundOn ? <Volume2 size={16}/> : <VolumeX size={16}/>} {soundOn ? "声音已开" : "声音已关"}</button>
        <p className="eyebrow">03 · 凭直觉选出 3 张</p><h1>{chosen.length === 3 ? "选择已完成" : `还需要选择 ${3 - chosen.length} 张`}</h1><p className="lead">{scenario === "方向探索" ? "三张牌依次代表：优势线索、适配环境、探索方向。" : "三张牌依次代表：当前处境、隐藏卡点、下一步行动。"}</p>
        <div className="spread">{Array.from({ length: slotCount }, (_, index) => <button key={index} className={`slot ${chosen.includes(index) ? "selected" : ""}`} onClick={() => chooseSlot(index)} aria-label={`第${index + 1}张牌`}>{chosen.includes(index) && <span>{chosen.indexOf(index) + 1}</span>}</button>)}</div>
        {error && <p className="error">{error}</p>}
        <button className="primary" disabled={chosen.length !== 3 || !!loading} onClick={() => void revealCards()}>{loading || "确认这 3 张"}</button>
      </section>}

      {step === "reveal" && <section className="stage wide center">
        <button className="stage-sound-toggle" onClick={toggleSound}>{soundOn ? <Volume2 size={16}/> : <VolumeX size={16}/>} {soundOn ? "声音已开" : "声音已关"}</button>
        <p className="eyebrow">04 · 翻开你的职场线索</p><h1>{revealed < 3 ? `第 ${revealed + 1} 张，${(scenario === "方向探索" ? ["看见优势", "看见环境", "看见方向"] : ["看见当下", "看见卡点", "看见行动"])[revealed]}` : "你的三张牌已出现"}</h1>
        <div className="reveal-row">{cards.map((card, index) => <div className="revealed-wrap" key={card.id}><button className="card-button" onClick={() => index === revealed && flipNext()} disabled={index !== revealed}><CardFace card={card} revealed={index < revealed} /></button>{index < revealed && <p><b>{card.position} · {card.name}{card.orientation}</b><span>{card.brief}</span></p>}</div>)}</div>
        {revealed < 3 ? <button className="primary" onClick={flipNext}>翻开第 {revealed + 1} 张</button> : <div className="gate-box"><div><Sparkles /><span><b>结合你的具体情况生成完整职场解读</b><small>只在生成成功内容时消耗 1 次</small></span></div><button className="primary" disabled={!!loading} onClick={() => void generateReport()}>{loading || "生成完整解读 · 1次"}</button></div>}
        {error && <p className="error">{error}</p>}
      </section>}

      {step === "reading" && report && <section className="stage reading">
        <div className="reading-head"><div><p className="eyebrow">你的本次职场复盘</p><h1>{report.title}</h1><p className="lead">{report.summary}</p></div><div className="reading-actions"><button className="secondary xhs-trigger" onClick={() => setShareMode("report")}><Share2 size={17} />分享到小红书</button><button className="secondary" onClick={restart}><RotateCcw size={17} />再问一次</button></div></div>
        <div className="mini-cards">{cards.map((card) => <div key={card.id}><CardFace card={card} compact /><span>{card.position}</span></div>)}</div>
        <div className="reading-grid"><article><span>01</span><h2>{scenario === "方向探索" ? "你已经具备的线索" : "你正在经历什么"}</h2><p>{report.situation}</p></article><article><span>02</span><h2>{scenario === "方向探索" ? "更适合你的环境" : "真正的卡点"}</h2><p>{report.blindSpot}</p></article><article><span>03</span><h2>{scenario === "方向探索" ? "优先验证的方向" : "可以推进的方向"}</h2><p>{report.advice}</p></article></div>
        <section className="next-actions"><p className="eyebrow">未来 48 小时</p><h2>先完成这三件小事</h2><ol>{report.next48Hours.map((item, index) => <li key={item}><span>{index + 1}</span>{item}</li>)}</ol></section>
        <div className="two-notes"><article><h3>留意这个风险</h3><p>{report.watchOut}</p></article><article><h3>留给你的复盘问题</h3><p>{report.reflection}</p></article></div>
        <section className="artifact-box"><div><p className="eyebrow">把洞察变成可执行材料</p><h2>再生成一份具体行动方案</h2><p className="muted">这是一项新的交付内容，将另外消耗 1 次。</p></div><div className="artifact-controls"><select value={artifactType} onChange={(e) => setArtifactType(e.target.value)}>{activeArtifactOptions.map((item) => <option key={item}>{item}</option>)}</select><button className="primary" disabled={!!loading} onClick={() => void generateArtifact()}>{loading || "生成行动方案 · 1次"}</button></div></section>
        {error && <p className="error">{error}</p>}
        {artifact && <section className="artifact-result"><div className="artifact-title"><div><p className="eyebrow">可执行方案</p><h2>{artifact.title}</h2></div><button className="artifact-share" onClick={() => setShareMode("artifact")} aria-label="把行动方案分享到小红书"><Share2 /><span>小红书笔记</span></button></div><p className="artifact-goal"><b>目标：</b>{artifact.goal}</p><div className="steps">{artifact.steps.map((item, index) => <article key={`${item.when}-${index}`}><span>{index + 1}</span><div><small>{item.when}</small><h3>{item.action}</h3><p>完成标准：{item.done}</p></div></article>)}</div><div className="script"><h3>可直接使用的话术</h3><p>{artifact.script}</p></div><p className="stop-rule"><b>止损线：</b>{artifact.stopRule}</p></section>}
      </section>}
    </main>

    <footer><ShieldCheck size={15} />内容用于自我复盘与决策参考，不替代专业、法律、医疗或财务意见。</footer>
    <ActivationModal open={activationOpen} onClose={() => setActivationOpen(false)} onActivated={setSession} />
    <XhsShareModal key={shareMode ?? "closed"} open={shareMode !== null} onClose={() => setShareMode(null)} scenario={scenario} cards={cards} report={report} artifact={shareMode === "artifact" ? artifact : null} />
  </div>;
}

function Welcome({ onStart }: { onStart: () => void }) {
  return <><section className="hero"><div className="hero-copy"><p className="eyebrow">亲手抽卡的职场决策工具</p><h1>当答案不在别人嘴里，<br /><em>先听见自己的判断。</em></h1><p>用熟悉的职场原型与三张线索卡，把象征线索翻译成具体的问题、边界与下一步行动。</p><button className="primary hero-button" onClick={onStart}>开始一次职场抽卡 <ArrowRight /></button><div className="trust"><span><ShieldCheck />不保存你的问题</span><span><Sparkles />78张完整答案卡</span><span><Target />行动导向解读</span></div></div><div className="hero-visual"><img src="/assets/career-tarot-hero.png" alt="原创职场答案卡卡背，包含组织结构、阶梯与天平符号" /><span className="orbit one">月亮<br /><small>信息迷雾</small></span><span className="orbit two">皇帝<br /><small>规则权力</small></span><span className="orbit three">塔<br /><small>结构突变</small></span></div></section><section className="how"><p className="eyebrow">不是一句随机答案</p><h2>你亲自完成每一步</h2><div><article><span>01</span><h3>说出困惑</h3><p>把模糊内耗收束成一个具体问题。</p></article><article><span>02</span><h3>洗牌叫停</h3><p>由你决定何时停下，再凭直觉选三张。</p></article><article><span>03</span><h3>看见行动</h3><p>从处境、卡点、行动三个位置完成复盘。</p></article></div></section></>;
}

function saveHistory(report: Reading, cards: DrawnCard[], question: string, scenario: Scenario) {
  try {
    const key = "career_tarot_history";
    const history = JSON.parse(localStorage.getItem(key) || "[]") as unknown[];
    history.unshift({ createdAt: Date.now(), scenario, question, cards, report });
    localStorage.setItem(key, JSON.stringify(history.slice(0, 10)));
  } catch { /* local history is optional */ }
}
